'use client';

import { FC, useCallback } from 'react';
import {
  getTradeAssetSelectorTranslations,
  IconMapper,
} from '../ChangeCurrencyOrSwap/changeCurrencyUtils';
import { AssetCell } from '../../common/components/AssetCell/AssetCell';
import {
  formatAssetAmount,
  getAssetAmountText,
} from '../../transactions/TransactionItem/transactionItemUtils';
import { Locale, useClientTranslation } from '@bts-web/utils-lokalise';
import { AlertBox } from '../../common';
import { AssetSelectDrawer } from '../../common/components/Drawers/AssetSelectDrawer/AssetSelectDrawer';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { useClientUserInfo } from '../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import {
  ASSET_PRICE_LOW_VALUE_THRESHOLD,
  intlFiatValueFormatting,
} from '@bts-web/utils-formatting';
import { TradeControllerVariants } from '../utils';
import { TFunction } from 'i18next';
import { useModalDrawerContext } from '../../common/components/ModalDrawerContext/ModalDrawerContext';
import { MutableNonNullableAsset } from '../CreateTrade/types';
import { TradeAssetDetails } from '../CreateTrade/MainTradeForm/types';
import { useFeatureFlag } from '../../statsig/sdk/provider';
import { StakingAssetSelectDrawer } from '../../staking/components/modalsDrawers/StakingAssetSelectDrawer/StakingAssetSelectDrawer';
import { TargetAssetDivider } from './TargetAssetDivider';
import { Divider } from '@bts-web/core-features/common';
import { FiatWalletController } from '../Fiat/Wallet/FiatWalletController';

interface TradeAssetSelectorProps {
  selectedAssetData: MutableNonNullableAsset;
  amountInput: string;
  targetAssetData?: MutableNonNullableAsset;
  locale: string;
  overrideOperationWithVariant?: TradeControllerVariants;
}

// Utility functions
const formatCryptoValue = (
  asset: TradeAssetDetails,
  locale: string,
): string | undefined => {
  if (!asset) return undefined;

  const amountLabel =
    asset.__typename === 'MetalAsset' ? ((asset.symbol as string) ?? '') : '';

  return getAssetAmountText(
    formatAssetAmount(asset.portfolio?.assetBalance, locale as Locale),
    amountLabel,
    asset.__typename,
    asset.symbol as string,
  );
};

const formatFiatValue = (
  asset: TradeAssetDetails,
  locale: string,
  fiatSymbol: string,
): string | undefined => {
  if (!asset?.price) return undefined;

  return intlFiatValueFormatting(
    asset.price.value,
    {
      locale,
      currency: fiatSymbol,
      fractionDigits: asset.price.precision,
    },
    ASSET_PRICE_LOW_VALUE_THRESHOLD,
  );
};

const TradeAssetSelector: FC<TradeAssetSelectorProps> = ({
  selectedAssetData,
  targetAssetData,
  locale,
  overrideOperationWithVariant,
  amountInput,
}) => {
  const params = useParams();

  const feature_hasSwapTradeEnabled = useFeatureFlag(
    'feature_hasSwapTradeEnabled',
  );

  const feature_hasStakingEnabled = useFeatureFlag('feature_hasStakingEnabled');

  const feature_enableFiatTransfers = useFeatureFlag(
    'feature_enableFiatTransfers',
  );

  const { t } = useClientTranslation();

  const translations = getTradeAssetSelectorTranslations(t as TFunction);

  const operation =
    overrideOperationWithVariant ||
    (params?.operation as TradeControllerVariants);

  const { openDrawer } = useModalDrawerContext();

  const { fiat } = useClientUserInfo();

  const searchParams = useSearchParams();

  const router = useRouter();

  // Navigation helpers
  const navigateTo = useCallback((path: string) => {
    router.push(path);
  }, []);

  const updateSwapParams = useCallback(
    (params: { [key: string]: string }) => {
      const newSearchParams = new URLSearchParams(
        searchParams?.toString() || '',
      );

      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          newSearchParams.set(key, value);
        } else {
          newSearchParams.delete(key);
        }
      });

      navigateTo(`${window.location.pathname}?${newSearchParams.toString()}`);
    },
    [searchParams],
  );

  const goToAssetDetails = useCallback(
    (assetId: string, operationType: string, queryParams?: string) => {
      const base = NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetId);

      navigateTo(
        `${base}/${operationType}${queryParams ? `?${queryParams}` : ''}`,
      );
    },
    [],
  );

  // Asset selection handlers
  const handleSourceAssetClick = useCallback(() => {
    if (operation === 'stake' || operation === 'unstake') {
      openDrawer(StakingAssetSelectDrawer, {
        isUnstaking: operation === 'unstake',
      });
    } else {
      openDrawer(AssetSelectDrawer, {
        onSelect(asset) {
          if (asset.symbol === fiat.symbol) {
            // FIAT selected as source
            if (targetAssetData?.id) {
              goToAssetDetails(targetAssetData.id, 'buy');
            } else {
              navigateTo(`${NEXT_ROUTES.DISCOVER}?next=buy`);
            }
          } else if (operation === 'buy') {
            // Non-FIAT source in buy mode -> swap
            goToAssetDetails(
              asset.id,
              'swap',
              `swapWith=${selectedAssetData.id}`,
            );
          } else if (operation === 'sell') {
            // Change asset in sell mode
            goToAssetDetails(asset.id, 'sell');
          } else if (targetAssetData?.id) {
            if (asset.id === targetAssetData.id) {
              // Same asset selected for source and destination
              goToAssetDetails(asset.id, 'swap');

              return;
            }

            // Swap with existing destination
            goToAssetDetails(
              asset.id,
              'swap',
              `swapWith=${targetAssetData.id}`,
            );
          } else {
            // Regular swap
            goToAssetDetails(asset.id, 'swap');
          }
        },
        ownedOnly: true,
        title: operation === 'sell' ? translations.sell : translations.payWith,
        showFiatOption: true,
        selectedAssetId: selectedAssetData?.id,
      });
    }
  }, [
    fiat.symbol,
    operation,
    targetAssetData,
    selectedAssetData.id,
    translations,
  ]);

  const handleDestinationAssetClick = useCallback(() => {
    openDrawer(AssetSelectDrawer, {
      onSelect(asset) {
        if (asset.symbol === fiat.symbol) {
          // FIAT as destination -> sell
          goToAssetDetails(selectedAssetData.id, 'sell');
        } else if (operation === 'buy') {
          // Change destination in buy mode
          goToAssetDetails(asset.id, 'buy');
        } else if (operation === 'sell') {
          // Non-FIAT destination in sell mode -> swap
          goToAssetDetails(
            selectedAssetData.id,
            'swap',
            `swapWith=${asset.id}`,
          );
        } else {
          if (asset.id === selectedAssetData.id) {
            // Same asset selected for source and destination
            goToAssetDetails(asset.id, 'swap');

            return;
          }

          // Update destination in swap mode
          updateSwapParams({ swapWith: asset.id });
        }
      },
      showFiatOption: true,
      title: translations.get,
      selectedAssetId: selectedAssetData?.id,
      showDisclaimer: true,
    });
  }, [
    operation,
    selectedAssetData.id,
    targetAssetData?.id,
    translations,
    fiat.symbol,
  ]);

  const renderBuyUI = () => (
    <>
      {feature_hasSwapTradeEnabled && (
        <>
          <AssetCell
            dataTestId="buy-source-fiat"
            icon={
              fiat.symbol
                ? IconMapper[fiat.symbol as keyof typeof IconMapper]
                : undefined
            }
            name={fiat.name ?? 'FIAT'}
            nameDescription={translations.payWith}
            onClick={handleSourceAssetClick}
          />
          <Divider />
        </>
      )}

      {feature_enableFiatTransfers && (
        <>
          <FiatWalletController
            amountInput={amountInput}
            currency={fiat.symbol || ''}
            locale={locale}
          />
        </>
      )}
    </>
  );

  const renderSellUI = () => (
    <>
      <AssetCell
        dataTestId="sell-source-asset"
        name={selectedAssetData.name ?? 'N/A'}
        nameDescription={translations.sell}
        valueDescription={translations.available}
        logoUrl={selectedAssetData.logoUrl as string}
        value={formatCryptoValue(
          selectedAssetData as unknown as TradeAssetDetails,
          locale,
        )}
        onClick={handleSourceAssetClick}
      />
      <TargetAssetDivider />
      {feature_hasSwapTradeEnabled && (
        <>
          <AssetCell
            dataTestId="sell-target-fiat"
            icon={
              fiat.symbol
                ? IconMapper[fiat.symbol as keyof typeof IconMapper]
                : undefined
            }
            name={fiat.name ?? 'FIAT'}
            nameDescription={translations.get}
            onClick={handleDestinationAssetClick}
          />
          <TargetAssetDivider />
        </>
      )}
    </>
  );

  const renderStakeUI = () => (
    <AssetCell
      dataTestId="change-stake-asset"
      name={selectedAssetData.name ?? 'N/A'}
      nameDescription={operation === 'unstake' ? t('unstaking') : t('staking')}
      valueDescription={translations.available}
      logoUrl={selectedAssetData.logoUrl as string}
      value={getAssetAmountText(
        formatAssetAmount(selectedAssetData.portfolio?.assetBalance, locale),
        '',
        selectedAssetData.__typename,
        selectedAssetData.symbol as string,
      )}
      onClick={handleSourceAssetClick}
    />
  );

  const renderSwapUI = () => (
    <>
      <AssetCell
        dataTestId="swap-source-asset"
        name={selectedAssetData.name ?? 'N/A'}
        nameDescription={translations.payWith}
        valueDescription={translations.available}
        logoUrl={selectedAssetData.logoUrl as string}
        value={formatCryptoValue(
          selectedAssetData as unknown as TradeAssetDetails,
          locale,
        )}
        onClick={handleSourceAssetClick}
      />
      <TargetAssetDivider />
      <AssetCell
        dataTestId="swap-target-asset"
        name={targetAssetData?.name ?? translations.select_asset}
        nameDescription={translations.get}
        valueDescription={targetAssetData ? translations.price : undefined}
        logoUrl={targetAssetData?.logoUrl as string}
        value={
          targetAssetData
            ? formatFiatValue(
                targetAssetData as unknown as TradeAssetDetails,
                locale,
                fiat.symbol as string,
              )
            : undefined
        }
        onClick={handleDestinationAssetClick}
      />
      {targetAssetData && !targetAssetData.isBuyActive && (
        <AlertBox variant="warning">{translations.assetUnavailable}</AlertBox>
      )}
      <TargetAssetDivider />
    </>
  );

  if (operation === 'buy') return renderBuyUI();

  if (operation === 'sell') return renderSellUI();

  if (
    feature_hasStakingEnabled &&
    (operation === 'stake' || operation === 'unstake')
  ) {
    return renderStakeUI();
  }

  if (feature_hasSwapTradeEnabled && operation === 'swap') {
    return renderSwapUI();
  }

  return null;
};

export { TradeAssetSelector };
